import { useContext, useEffect, useReducer } from 'react'

import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import {
  emptyFrameBuilderState,
  FrameBuilderSelectedStateContext,
} from '@frame-builder/contexts/state-context'
import { frameBuilderStateReducer } from '@frame-builder/lib/state-reducer'
import { type FrameBuilderState } from '@frame-builder/lib/types'
import { getStateFrameSize } from '../size'
import {
  getIsProfileValid,
  getIsPassepartoutColorValid,
  getIsSpacerSizeValid,
  getIsTextureValid,
} from './state-validation'

/**
 * Saves last valid frame builder state when validation fails.
 */
export const useLastValidFrameBuilderState = (): FrameBuilderState => {
  const { frameBuilderConfiguration, frameBuilderType } = useContext(
    FrameBuilderSettingsContext,
  )
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )

  const [lastValidState, dispatchLastValidFrameBuilderState] = useReducer(
    frameBuilderStateReducer,
    emptyFrameBuilderState,
  )

  // Deselect items if they have become invalid
  useEffect(() => {
    const profile = selectedState.profile ?? lastValidState.profile

    // Deselect mount color
    const isPassepartoutColorValid = getIsPassepartoutColorValid(
      frameBuilderType,
      selectedState.profile,
      selectedState.passepartout.color,
    )

    if (selectedState.passepartout.color && !isPassepartoutColorValid) {
      dispatchLastValidFrameBuilderState({
        type: 'passepartout',
        color: selectedState.passepartout.color,
      })
      dispatchSelectedState({
        type: 'passepartout',
        color: undefined,
      })
    }

    // Deselect moulding
    const frameSize = getStateFrameSize(selectedState)
    const isProfileValid = getIsProfileValid(frameSize, selectedState.profile)

    if (selectedState.profile && !isProfileValid) {
      dispatchLastValidFrameBuilderState({
        type: 'frameStyle',
        profile: selectedState.profile,
      })
      dispatchSelectedState({
        type: 'frameStyle',
        profile: undefined,
      })
    }

    // Deselect texture
    const isTextureValid = getIsTextureValid(
      frameBuilderConfiguration,
      profile,
      selectedState.texture,
    )

    if (selectedState.texture && !isTextureValid) {
      const defaultTexture = frameBuilderConfiguration.textures.find(
        (texture) => texture._id === profile?.defaultTexture?._id,
      )
      const isDefaultTextureValid = defaultTexture
        ? getIsTextureValid(frameBuilderConfiguration, profile, defaultTexture)
        : false

      // Select default texture
      if (defaultTexture && isDefaultTextureValid) {
        dispatchSelectedState({
          type: 'frameStyle',
          texture: defaultTexture,
        })
        dispatchLastValidFrameBuilderState({
          type: 'frameStyle',
          texture: undefined,
        })

        return
      }

      // Deselect texture, if there is no valid default texture
      dispatchLastValidFrameBuilderState({
        type: 'frameStyle',
        texture: selectedState.texture,
      })
      dispatchSelectedState({
        type: 'frameStyle',
        texture: undefined,
      })
    }

    // Deselect spacer size
    const isSpacerSizeValid = getIsSpacerSizeValid(
      selectedState.profile,
      selectedState.spacerSize,
    )

    if (selectedState.spacerSize && !isSpacerSizeValid) {
      dispatchLastValidFrameBuilderState({
        type: 'spacer',
        size: selectedState.spacerSize,
      })
      dispatchSelectedState({
        type: 'spacer',
        size: undefined,
      })
    }
  }, [
    dispatchLastValidFrameBuilderState,
    dispatchSelectedState,
    frameBuilderConfiguration,
    frameBuilderType,
    lastValidState,
    selectedState,
  ])

  // Reselect items if they have become valid again
  useEffect(() => {
    const profile = selectedState.profile ?? lastValidState.profile

    // Reselect mount color
    const isPassepartoutColorValid = getIsPassepartoutColorValid(
      frameBuilderType,
      selectedState.profile,
      lastValidState.passepartout.color,
    )

    if (lastValidState.passepartout.color && isPassepartoutColorValid) {
      dispatchSelectedState({
        type: 'passepartout',
        color: lastValidState.passepartout.color,
      })
      dispatchLastValidFrameBuilderState({
        type: 'passepartout',
        color: undefined,
      })
    }

    // Reselect moulding
    const frameSize = getStateFrameSize(selectedState)
    const isProfileValid = getIsProfileValid(frameSize, lastValidState.profile)

    if (lastValidState.profile && isProfileValid) {
      dispatchSelectedState({
        type: 'frameStyle',
        profile: lastValidState.profile,
      })
      dispatchLastValidFrameBuilderState({
        type: 'frameStyle',
        profile: undefined,
      })
    }

    // Reselect texture
    const isTextureValid = getIsTextureValid(
      frameBuilderConfiguration,
      profile,
      lastValidState.texture,
    )

    if (lastValidState.texture && isTextureValid) {
      dispatchSelectedState({
        type: 'frameStyle',
        texture: lastValidState.texture,
      })
      dispatchLastValidFrameBuilderState({
        type: 'frameStyle',
        texture: undefined,
      })
    }

    // Reselect spacer size
    const isSpacerSizeValid = getIsSpacerSizeValid(
      selectedState.profile,
      lastValidState.spacerSize,
    )

    if (lastValidState.spacerSize && isSpacerSizeValid) {
      dispatchSelectedState({
        type: 'spacer',
        size: lastValidState.spacerSize,
      })
      dispatchLastValidFrameBuilderState({
        type: 'spacer',
        size: undefined,
      })
    }
  }, [
    dispatchLastValidFrameBuilderState,
    dispatchSelectedState,
    frameBuilderConfiguration,
    frameBuilderType,
    lastValidState,
    selectedState,
  ])

  return lastValidState
}
